import React, { useState, useEffect } from 'react';
import { FaChevronRight, FaChevronDown, FaIndustry, FaCog, FaThermometerHalf, FaExclamationTriangle } from 'react-icons/fa';
import './TreeView.css';

const TreeView = ({ assetData, currentStatus }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set(['root']));
  const [selectedNode, setSelectedNode] = useState(null);

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal':
        return '#00C851';
      case 'warning':
        return '#FF8800';
      case 'critical':
        return '#FF4444';
      default:
        return '#B0B0B0';
    }
  };

  const isExpanded = (nodeId) => expandedNodes.has(nodeId);

  const renderSensorValue = (sensorKey, reading) => {
    return (
      <div
        key={sensorKey}
        className={`tree-node tree-leaf ${selectedNode === sensorKey ? 'selected' : ''}`}
        onClick={() => setSelectedNode(sensorKey)}
      >
        <span className="tree-indent"></span>
        <span className="tree-indent"></span>
        <span className="tree-icon leaf">
          <FaThermometerHalf />
        </span>
        <span className="tree-label">{sensorKey}</span>
        <span className="tree-value" style={{ color: getStatusColor(reading.status) }}>
          {reading.value} {reading.unit}
        </span>
      </div>
    );
  };

  const renderComponent = (asset, component, componentIndex) => {
    const componentId = `${asset.id}_${component.id}`;
    const expanded = isExpanded(componentId);
    const isAffected = asset.affectedComponents && asset.affectedComponents.includes(component.id);

    return (
      <div key={componentId}>
        <div
          className={`tree-node ${isAffected ? 'affected' : ''} ${selectedNode === componentId ? 'selected' : ''}`}
          onClick={() => {
            toggleNode(componentId);
            setSelectedNode(componentId);
          }}
        >
          <span className="tree-indent"></span>
          <span className="tree-toggle">
            {expanded ? <FaChevronDown /> : <FaChevronRight />}
          </span>
          <span className="tree-icon component">
            <FaCog />
          </span>
          <span className="tree-label">{component.name}</span>
          {isAffected && (
            <span className="affected-badge" style={{ color: getStatusColor(asset.status) }}>
              <FaExclamationTriangle />
            </span>
          )}
          {component.critical && (
            <span className="critical-badge">!</span>
          )}
        </div>

        {expanded && (
          <div className="tree-children">
            {Object.keys(asset.readings).slice(
              componentIndex * 2,
              componentIndex * 2 + 2
            ).map(sensorKey => 
              renderSensorValue(sensorKey, asset.readings[sensorKey])
            )}
          </div>
        )}
      </div>
    );
  };

  const renderAsset = (asset) => {
    const assetId = asset.id;
    const expanded = isExpanded(assetId);

    return (
      <div key={assetId}>
        <div
          className={`tree-node ${selectedNode === assetId ? 'selected' : ''}`}
          onClick={() => {
            toggleNode(assetId);
            setSelectedNode(assetId);
          }}
        >
          <span className="tree-toggle">
            {expanded ? <FaChevronDown /> : <FaChevronRight />}
          </span>
          <span className="tree-icon asset">
            <FaIndustry />
          </span>
          <span className="tree-label">{asset.name}</span>
          <span 
            className="tree-status-dot"
            style={{ backgroundColor: getStatusColor(asset.status) }}
          ></span>
        </div>

        {expanded && (
          <div className="tree-children">
            {asset.components.map((component, index) => 
              renderComponent(asset, component, index)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tree-view-container">
      <div className="tree-header">
        <h3>Anlagenstruktur</h3>
        <button 
          className="expand-all-btn"
          onClick={() => {
            if (expandedNodes.size > 1) {
              setExpandedNodes(new Set(['root']));
            } else {
              const allNodes = new Set(['root']);
              Object.values(assetData).forEach(asset => {
                allNodes.add(asset.id);
                asset.components.forEach(comp => {
                  allNodes.add(`${asset.id}_${comp.id}`);
                });
              });
              setExpandedNodes(allNodes);
            }
          }}
        >
          {expandedNodes.size > 1 ? 'Alle zuklappen' : 'Alle aufklappen'}
        </button>
      </div>
      
      <div className="tree-content">
        {Object.values(assetData).map(asset => renderAsset(asset))}
      </div>
    </div>
  );
};

export default TreeView;
